import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { useTheme } from './ThemeProvider';

interface VoiceReplyBubbleProps {
  audioUrl: string;
  duration?: number;
  className?: string;
  onRemove?: () => void;
}

export function VoiceReplyBubble({ audioUrl, duration: totalDuration, className = "", onRemove }: VoiceReplyBubbleProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(totalDuration || 0);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { theme } = useTheme();
  
  // 🔄 Prop Sync: If totalDuration is passed (e.g. from recorder or DB), sync it initially
  // This prevents the "0:00" or "0:10" flicker before metadata loads.
  useEffect(() => {
    if (totalDuration && totalDuration > 0) {
      setDuration(totalDuration);
    }
  }, [totalDuration]);

  // 🔄 Force Sync: If audio says it's playing but time is stuck, trigger a reload/seek
  const forceSyncRef = useRef<number | null>(null);

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      try {
        // 🚀 Capacitor/iOS Optimization: Pre-load the source if not ready
        if (audioRef.current.readyState < 2) {
          audioRef.current.load();
        }
        
        // Reset to start if finished
        if (audioRef.current.ended) {
          audioRef.current.currentTime = 0;
        }

        await audioRef.current.play();

        // 🛡️ Force Sync Check: If after 500ms we are "playing" but currentTime is still 0, 
        // the native engine might be hitched. Trigger one re-seek.
        if (forceSyncRef.current) clearTimeout(forceSyncRef.current);
        forceSyncRef.current = window.setTimeout(() => {
          if (audioRef.current && !audioRef.current.paused && audioRef.current.currentTime === 0) {
            console.log("[VoiceReplyBubble] Playback hitch detected. Forcing seek to 0.01s...");
            audioRef.current.currentTime = 0.01; // Tiny seek often wakes up the WKWebView engine
          }
        }, 500);

      } catch (err) {
        console.error("[VoiceReplyBubble] Playback failed:", err);
        // Fallback: try loading again
        audioRef.current.load();
      }
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || duration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * (duration / 1000);
    audioRef.current.currentTime = newTime;
  };

  // 🏁 STYLING:
  const gradientClass = theme === 'dark' 
    ? 'bg-[#4478ea] bg-linear-to-r from-[#4478ea] to-[#2d1ef1] shadow-blue-500/20' 
    : 'bg-[#F39C12] bg-linear-to-r from-[#F39C12] to-[#E74C3C] shadow-orange-500/20';

  return (
    <div className={`p-3 rounded-2xl ${gradientClass} text-white flex items-center gap-3 shadow-lg min-w-[220px] max-w-[280px] group transition-all hover:scale-[1.02] ${className}`}>
      {/* 🎙️ THE NATIVE ENGINE */}
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime * 1000);
          }
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) {
            const realDuration = audioRef.current.duration;
            // Only use real duration if it's a valid finite number (some containers report Infinity)
            if (isFinite(realDuration) && realDuration > 0) {
              setDuration(realDuration * 1000); // Always trust the file, not the JS timer
            } else if (totalDuration) {
              setDuration(totalDuration); // Fallback to stored timer estimate
            }
          }
        }}
      />

      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          togglePlay();
        }}
        className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white shrink-0 flex items-center justify-center relative active:scale-95 transition-transform"
      >
        {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current translate-x-0.5" />}
      </Button>

      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex items-center gap-1 text-[11px] px-1 font-bold tabular-nums">
          <span className="opacity-100">{formatTime(currentTime)}</span>
          <span className="opacity-60">/</span>
          <span className="opacity-70">{formatTime(duration)}</span>
        </div>
        
        {/* Simple Progress bar */}
        <div 
          className="h-2 bg-white/20 rounded-full overflow-hidden relative cursor-pointer"
          onClick={handleSeek}
        >
          <div 
            className="h-full bg-white transition-all duration-100 ease-linear shadow-[0_0_8px_rgba(255,255,255,0.6)]"
            style={{ width: `${(currentTime / duration) * 100}%` }}
          />
        </div>
      </div>

      <div className="shrink-0 opacity-40 group-hover:opacity-80 transition-opacity flex items-center gap-2">
        {onRemove && (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        )}
        <Volume2 className="w-4 h-4 pr-1" />
      </div>
    </div>
  );
}
